/**
 * LowPipelineLabel (PLAN 8.4)
 *
 * "Low pipeline · runs dry Oct 22" under a brand's runway bar, with the explaining tooltip. Reads the server's
 * content-cover fields only (utils/lowPipeline.ts); renders nothing for brands that are not low.
 */

import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import type { PortfolioClient } from '@/data/portfolio.types';
import { formatRunsDryOn, isLowPipeline } from '@/utils/lowPipeline';

export const LowPipelineLabel = ({ client }: { client: PortfolioClient }) => {
  const { t, i18n } = useTranslation('agency');
  if (!isLowPipeline(client)) return null;

  const date = formatRunsDryOn(client.runsDryOn, i18n.language);
  const label = t('low_pipeline_label', { defaultValue: 'Low pipeline · runs dry around {{date}}', date });
  const tooltip = t('low_pipeline_tooltip', {
    defaultValue: 'Less than 2 weeks of planned and ready content. Runs dry around {{date}}.',
    date,
  });

  return (
    <Tooltip content={tooltip} side="top">
      <span
        className="mt-1 inline-flex max-w-full items-center gap-1 text-[10px] font-medium leading-tight text-coral-700"
        aria-label={`${label}. ${tooltip}`}
      >
        <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-coral-500" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </span>
    </Tooltip>
  );
};
